import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SafeIcon from '../../common/SafeIcon';
import { mockResources } from '../../data/mockData';
import {
  fetchArticles, createArticle, updateArticle, deleteArticle,
  fetchHelpArticles, createHelpArticle, updateHelpArticle, deleteHelpArticle,
  fetchFeaturedVideos, createFeaturedVideo, updateFeaturedVideo, deleteFeaturedVideo,
  updateItemSortOrder,
} from '../../services/resourcesEditorService';

const {
  FiEdit3, FiTrash2, FiPlus, FiSave, FiX, FiVideo, FiFileText,
  FiImage, FiLink, FiGrid, FiSettings, FiCheck, FiAlertTriangle,
  FiRefreshCw, FiStar, FiZap, FiCheckCircle, FiDownloadCloud, FiType,
  FiChevronUp, FiChevronDown,
} = FiIcons;

// ── Mock seed data ────────────────────────────────────────────────────────────
const MOCK_VIDEOS = [
  {
    title: 'Getting Started with OneFinalLegacy',
    content: '<p>A quick walkthrough of creating your first memorial page and inviting family members.</p>',
    category: 'Platform Tutorial',
    youtube_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    icon: '🎥',
    sort_order: 1,
  },
  {
    title: 'Understanding Grief — A Compassionate Guide',
    content: '<p>Mental health professionals share evidence-based strategies for navigating loss.</p>',
    category: 'Grief Support',
    youtube_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
    icon: '❤️',
    sort_order: 2,
  },
  {
    title: 'Digital Legacy Planning: What You Need to Know',
    content: '<p>How to organise your digital assets, designate heirs, and protect your online presence.</p>',
    category: 'Estate Planning',
    youtube_url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
    icon: '🔐',
    sort_order: 3,
  },
];

const VIDEO_CATEGORIES   = ['Getting Started','Grief Support','Estate Planning','Platform Tutorial','Legal Guidance','Community'];
const ARTICLE_CATEGORIES = ['Articles','Funeral Planning','Legal & Will Prep','Estate Services','User Roles','Legal','About'];
const GRID_OPTIONS       = [{ label:'1 Column',cols:1 },{ label:'2 Columns',cols:2 },{ label:'3 Columns',cols:3 },{ label:'4 Columns',cols:4 }];
const FEATURED_POSITIONS = [
  { id:'top',    label:'Above Grid',    icon:'⬆️' },
  { id:'left',   label:'Left of Grid',  icon:'⬅️' },
  { id:'right',  label:'Right of Grid', icon:'➡️' },
  { id:'bottom', label:'Below Grid',    icon:'⬇️' },
];

// ── Quill config ──────────────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ header:[1,2,3,false] }],
    ['bold','italic','underline','strike'],
    [{ color:[] },{ background:[] }],
    [{ list:'ordered' },{ list:'bullet' }],
    [{ indent:'-1' },{ indent:'+1' }],
    ['blockquote','link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header','bold','italic','underline','strike','color','background','list','bullet','indent','blockquote','link'];

// ── Shared UI primitives ──────────────────────────────────────────────────────
const Field = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {hint && <span className="text-[9px] text-text-muted font-light">{hint}</span>}
    </div>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props}
    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none
               focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors" />
);

const Select = ({ children, ...props }) => (
  <select {...props}
    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none
               focus:border-accent-gold text-sm text-accent-navy transition-colors">
    {children}
  </select>
);

const Toggle = ({ value, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <div onClick={onChange}
      className={`w-11 h-6 rounded-full relative transition-colors duration-300 cursor-pointer ${value ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
      <motion.div animate={{ x: value ? 22 : 2 }} transition={{ type:'spring',stiffness:500,damping:30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" />
    </div>
    <span className="text-sm font-bold text-accent-navy">{label}</span>
  </label>
);

const RichEditor = ({ value, onChange, placeholder='Start writing…', minHeight=180 }) => (
  <div className="rounded-2xl overflow-hidden border-2 border-brand-dark focus-within:border-accent-gold transition-colors bg-white"
    style={{ minHeight: minHeight + 42 }}>
    <ReactQuill theme="snow" value={value || ''} onChange={onChange}
      modules={QUILL_MODULES} formats={QUILL_FORMATS} placeholder={placeholder}
      style={{ display:'flex', flexDirection:'column', minHeight: minHeight + 42 }} />
    <style>{`.ql-editor { min-height: ${minHeight}px !important; }`}</style>
  </div>
);

const FormatHint = () => (
  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-gold/10 border border-accent-gold/20 rounded-xl w-fit">
    <SafeIcon icon={FiType} className="w-3 h-3 text-accent-gold" />
    <span className="text-[9px] font-black uppercase tracking-widest text-accent-navy">
      Bold · Italic · Underline · Highlight · Lists · Links
    </span>
  </div>
);

const Spinner = ({ color='border-white' }) => (
  <div className={`w-4 h-4 border-2 border-opacity-30 ${color} border-t-current rounded-full animate-spin`} />
);

// ── Article Form Modal ────────────────────────────────────────────────────────
const ArticleFormModal = ({ article, onSave, onClose, saving }) => {
  const isNew = !article?.id;
  const [form, setForm] = useState({
    title:'', excerpt:'', content:'', category:'Articles',
    cover_image_url:'', youtube_url:'', tags:'', status:'published', is_featured:0,
    sort_order: 999,
    ...(article || {}),
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
      <motion.div initial={{ opacity:0,scale:.95,y:20 }} animate={{ opacity:1,scale:1,y:0 }} exit={{ opacity:0,scale:.95 }}
        className="bg-white rounded-[32px] w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white z-20 flex items-center justify-between px-8 py-6 border-b border-brand-dark rounded-t-[32px]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-accent-navy rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiFileText} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-accent-navy text-xl">{isNew ? 'New Article' : 'Edit Article'}</h3>
              <p className="text-[9px] text-text-muted font-light">Rich text formatting enabled</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-brand-light transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="p-8 space-y-6 flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Field label="Title" required>
                <Input value={form.title} onChange={set('title')} placeholder="Article title…" />
              </Field>
            </div>
            <Field label="Category">
              <Select value={form.category} onChange={set('category')}>
                {ARTICLE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={set('status')}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </Select>
            </Field>
          </div>

          <Field label="Excerpt" hint="Plain text — shown on article cards">
            <textarea rows={2} value={form.excerpt} onChange={set('excerpt')} placeholder="Brief summary…"
              className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors resize-none" />
          </Field>

          <Field label="Full Content" hint="Rich text — use toolbar to format">
            <div className="space-y-2">
              <FormatHint />
              <RichEditor value={form.content} onChange={(val) => setForm(f => ({ ...f, content: val }))}
                placeholder="Write the full article content here…" minHeight={220} />
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Cover Image URL">
              <div className="relative">
                <SafeIcon icon={FiImage} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input value={form.cover_image_url} onChange={set('cover_image_url')} placeholder="https://images.unsplash.com/…"
                  className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors" />
              </div>
            </Field>
            <Field label="YouTube URL" hint="Optional">
              <div className="relative">
                <SafeIcon icon={FiVideo} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input value={form.youtube_url} onChange={set('youtube_url')} placeholder="https://www.youtube.com/watch?v=…"
                  className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors" />
              </div>
            </Field>
          </div>

          {form.cover_image_url && (
            <div className="rounded-2xl overflow-hidden border border-brand-dark h-44">
              <img src={form.cover_image_url} alt="Cover preview" className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}

          <Field label="Tags" hint="Comma-separated">
            <Input value={form.tags} onChange={set('tags')} placeholder="grief, planning, estate…" />
          </Field>

          <div className="grid grid-cols-2 gap-5">
            <Field label="Sort Order">
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 999 }))} />
            </Field>
            <div className="flex items-end pb-1">
              <Toggle value={!!form.is_featured}
                onChange={() => setForm(f => ({ ...f, is_featured: f.is_featured ? 0 : 1 }))}
                label="Featured Article" />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-brand-dark px-8 py-5 flex justify-end gap-3 rounded-b-[32px] z-20">
          <button onClick={onClose}
            className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-text-muted text-sm font-black uppercase tracking-widest hover:border-accent-navy transition-all">
            Cancel
          </button>
          <button onClick={() => onSave(form)} disabled={!form.title.trim() || saving}
            className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-accent-gold transition-all disabled:opacity-50">
            {saving ? <><Spinner /> Saving…</> : <><SafeIcon icon={FiSave} className="w-4 h-4" /> {isNew ? 'Create Article' : 'Save Changes'}</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Video Form Modal ──────────────────────────────────────────────────────────
const VideoFormModal = ({ video, onSave, onClose, saving }) => {
  const isNew = !video?.id;
  const [form, setForm] = useState({
    title:'', content:'', category:'Getting Started',
    youtube_url:'', icon:'🎥', is_published:1, sort_order:10,
    page_context: '',
    ...(video || {}),
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const youtubeId = (url) => url?.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
  const thumbSrc  = youtubeId(form.youtube_url)
    ? `https://img.youtube.com/vi/${youtubeId(form.youtube_url)}/mqdefault.jpg`
    : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
      <motion.div initial={{ opacity:0,scale:.95,y:20 }} animate={{ opacity:1,scale:1,y:0 }} exit={{ opacity:0,scale:.95 }}
        className="bg-white rounded-[32px] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-8 py-6 border-b border-brand-dark rounded-t-[32px]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiVideo} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-accent-navy text-xl">{isNew ? 'New Video' : 'Edit Video'}</h3>
              <p className="text-[9px] text-text-muted font-light">Rich text description enabled</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-brand-light transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="p-8 space-y-5 flex-1">
          <Field label="Video Title" required>
            <Input value={form.title} onChange={set('title')} placeholder="Video title…" />
          </Field>

          <Field label="YouTube URL" required>
            <div className="relative">
              <SafeIcon icon={FiLink} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
              <input value={form.youtube_url} onChange={set('youtube_url')} placeholder="https://www.youtube.com/watch?v=…"
                className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors" />
            </div>
          </Field>

          {thumbSrc && (
            <div className="rounded-2xl overflow-hidden border-2 border-brand-dark h-36 bg-black">
              <img src={thumbSrc} alt="YouTube thumbnail" className="w-full h-full object-cover" />
            </div>
          )}

          <Field label="Description" hint="Rich text supported">
            <div className="space-y-2">
              <FormatHint />
              <RichEditor value={form.content} onChange={(val) => setForm(f => ({ ...f, content: val }))}
                placeholder="Describe what viewers will learn…" minHeight={140} />
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-5">
            <Field label="Category">
              <Select value={form.category} onChange={set('category')}>
                {VIDEO_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Sort Order">
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
            </Field>
          </div>

          <Field label="Icon Emoji">
            <Input value={form.icon} onChange={set('icon')} placeholder="🎥" />
          </Field>

          <Toggle value={!!form.is_published}
            onChange={() => setForm(f => ({ ...f, is_published: f.is_published ? 0 : 1 }))}
            label="Published (visible on Resources page)" />
        </div>

        <div className="sticky bottom-0 bg-white border-t border-brand-dark px-8 py-5 flex justify-end gap-3 rounded-b-[32px]">
          <button onClick={onClose}
            className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-text-muted text-sm font-black uppercase tracking-widest hover:border-accent-navy transition-all">
            Cancel
          </button>
          <button onClick={() => onSave(form)} disabled={!form.title.trim() || !form.youtube_url.trim() || saving}
            className="flex items-center gap-2 px-8 py-3 bg-red-500 text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-red-600 transition-all disabled:opacity-50">
            {saving ? <><Spinner /> Saving…</> : <><SafeIcon icon={FiSave} className="w-4 h-4" /> {isNew ? 'Add Video' : 'Save Changes'}</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Featured Video Form Modal ─────────────────────────────────────────────────
const FeaturedVideoFormModal = ({ featuredVideo, existingVideos, onSave, onClose, saving }) => {
  const isNew = !featuredVideo?.id;
  const [mode, setMode] = useState('new');
  const [form, setForm] = useState({
    title:'', content:'', description:'', youtube_url:'',
    category:'Getting Started', icon:'⭐', sort_order:1,
    is_active:1, promoted_from_id:'',
    ...(featuredVideo || {}),
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!isNew && featuredVideo?.promoted_from_id) setMode('promote');
  }, [isNew, featuredVideo]);

  const handlePromoteSelect = (vid) => {
    if (!vid) { setForm(f => ({ ...f, promoted_from_id:'' })); return; }
    setForm(f => ({
      ...f,
      promoted_from_id: String(vid.id),
      title:       vid.title       || '',
      content:     vid.content     || '',
      description: vid.content     || '',
      youtube_url: vid.youtube_url || '',
      category:    vid.category    || 'Getting Started',
      icon:        vid.icon        || '⭐',
    }));
  };

  const youtubeId = (url) => url?.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
  const thumbSrc  = youtubeId(form.youtube_url)
    ? `https://img.youtube.com/vi/${youtubeId(form.youtube_url)}/mqdefault.jpg`
    : null;

  const canSave = form.title.trim() && form.youtube_url.trim() &&
    (mode === 'new' || (mode === 'promote' && form.promoted_from_id));

  const handleSave = () => {
    const payload = {
      ...form,
      description: form.description || form.content || form.title,
      content:     form.description || form.content || form.title,
      promoted_from_id: (mode === 'promote' && form.promoted_from_id)
        ? String(form.promoted_from_id) : '',
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
      <motion.div initial={{ opacity:0,scale:.95,y:20 }} animate={{ opacity:1,scale:1,y:0 }} exit={{ opacity:0,scale:.95 }}
        className="bg-white rounded-[32px] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-8 py-6 border-b border-brand-dark rounded-t-[32px]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-accent-gold rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiStar} className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-accent-navy text-xl">{isNew ? 'Feature a Video' : 'Edit Featured Video'}</h3>
              <p className="text-[9px] text-text-muted font-light">Appears prominently on Resources page</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-brand-light transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="p-8 space-y-6 flex-1">
          {isNew && (
            <div className="flex gap-2 p-1 bg-brand-light rounded-2xl">
              {[
                { id:'new',     label:'✦ Brand New Video',  icon:FiZap         },
                { id:'promote', label:'↑ Promote Existing', icon:FiCheckCircle },
              ].map(m => (
                <button key={m.id}
                  onClick={() => { setMode(m.id); setForm(f => ({ ...f, promoted_from_id:'' })); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${mode === m.id ? 'bg-accent-navy text-white shadow-md' : 'text-text-muted hover:text-accent-navy'}`}>
                  <SafeIcon icon={m.icon} className="w-3.5 h-3.5" />{m.label}
                </button>
              ))}
            </div>
          )}

          {mode === 'promote' && isNew && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Select a video to promote</p>
              {existingVideos.length === 0 ? (
                <p className="text-sm text-text-muted font-light text-center py-6">No videos in library yet. Add some videos first.</p>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {existingVideos.map(vid => (
                    <button key={vid.id} onClick={() => handlePromoteSelect(vid)}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${String(form.promoted_from_id) === String(vid.id) ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark hover:border-accent-navy/30'}`}>
                      <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-lg flex-shrink-0">{vid.icon || '🎥'}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-accent-navy truncate">{vid.title}</p>
                        <p className="text-[10px] text-text-muted font-mono truncate">{vid.youtube_url}</p>
                      </div>
                      {String(form.promoted_from_id) === String(vid.id) && (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-accent-gold flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-5">
            <Field label="Featured Title" required>
              <Input value={form.title} onChange={set('title')} placeholder="e.g. Platform Walkthrough" />
            </Field>

            <Field label="YouTube URL" required>
              <div className="relative">
                <SafeIcon icon={FiLink} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input value={form.youtube_url} onChange={set('youtube_url')}
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm text-accent-navy placeholder:text-text-muted/40 transition-colors" />
              </div>
            </Field>

            {thumbSrc && (
              <div className="rounded-2xl overflow-hidden border-2 border-brand-dark h-40 bg-black">
                <img src={thumbSrc} alt="YouTube thumbnail" className="w-full h-full object-cover" />
              </div>
            )}

            <Field label="Description" hint="Rich text supported">
              <div className="space-y-2">
                <FormatHint />
                <RichEditor
                  value={form.description || form.content}
                  onChange={(val) => setForm(f => ({ ...f, description: val, content: val }))}
                  placeholder="Why is this video featured? What will viewers gain?"
                  minHeight={130}
                />
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-5">
              <Field label="Category">
                <Select value={form.category} onChange={set('category')}>
                  {VIDEO_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Sort Order">
                <Input type="number" value={form.sort_order}
                  onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 1 }))} />
              </Field>
            </div>

            <Field label="Icon Emoji">
              <Input value={form.icon} onChange={set('icon')} placeholder="⭐" />
            </Field>

            <Toggle value={!!form.is_active}
              onChange={() => setForm(f => ({ ...f, is_active: f.is_active ? 0 : 1 }))}
              label="Active (visible on Resources page)" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-brand-dark px-8 py-5 flex justify-end gap-3 rounded-b-[32px]">
          <button onClick={onClose}
            className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-text-muted text-sm font-black uppercase tracking-widest hover:border-accent-navy transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={!canSave || saving}
            className="flex items-center gap-2 px-8 py-3 bg-accent-gold text-accent-navy rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-accent-gold/80 transition-all disabled:opacity-50">
            {saving
              ? <><Spinner color="border-accent-navy" /> Saving…</>
              : <><SafeIcon icon={FiStar} className="w-4 h-4" /> {isNew ? 'Feature Video' : 'Save Changes'}</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Delete Confirm ────────────────────────────────────────────────────────────
const DeleteConfirm = ({ item, onConfirm, onCancel, deleting }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[400] flex items-center justify-center p-4">
    <motion.div initial={{ opacity:0,scale:.9 }} animate={{ opacity:1,scale:1 }}
      className="bg-white rounded-[28px] p-8 max-w-md w-full shadow-2xl">
      <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <SafeIcon icon={FiAlertTriangle} className="w-7 h-7 text-red-500" />
      </div>
      <h3 className="text-xl font-serif text-accent-navy text-center mb-3">Delete Permanently?</h3>
      <p className="text-sm text-text-muted text-center mb-6 font-light">
        "<strong>{item?.title}</strong>" will be permanently removed and cannot be recovered.
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 py-3 rounded-2xl border-2 border-brand-dark text-text-muted text-sm font-black uppercase tracking-widest hover:border-accent-navy transition-all">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={deleting}
          className="flex-1 py-3 bg-red-500 text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-red-600 transition-all disabled:opacity-50">
          {deleting ? 'Deleting…' : 'Yes, Delete'}
        </button>
      </div>
    </motion.div>
  </div>
);

// ── Empty / Ingest Banner ─────────────────────────────────────────────────────
const IngestBanner = ({ label, icon, onIngest, loading }) => (
  <div className="flex flex-col items-center justify-center py-16 bg-brand-light rounded-[28px] border-2 border-dashed border-brand-dark text-center">
    <SafeIcon icon={icon} className="w-12 h-12 text-text-muted/20 mx-auto mb-3" />
    <p className="font-serif text-accent-navy text-lg mb-1">No {label} yet</p>
    <p className="text-text-muted font-light text-sm mb-5">Load the built-in mock {label.toLowerCase()} to get started.</p>
    <button onClick={onIngest} disabled={loading}
      className="flex items-center gap-2 px-7 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all disabled:opacity-50">
      {loading
        ? <><Spinner /> Loading…</>
        : <><SafeIcon icon={FiDownloadCloud} className="w-4 h-4" /> Load Mock {label}</>}
    </button>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const AdminResourceEditor = ({ isOpen, onClose }) => {
  const [tab, setTab]                   = useState('articles');
  const [articles, setArticles]         = useState([]);
  const [videos, setVideos]             = useState([]);
  const [featured, setFeatured]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [ingestingArt, setIngestingArt] = useState(false);
  const [ingestingVid, setIngestingVid] = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [editingItem, setEditingItem]   = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [filterCat, setFilterCat]       = useState('All');
  const [toast, setToast]               = useState(null);

  const [gridCols, setGridCols]       = useState(() => Number(localStorage.getItem('admin_gridCols'))  || 3);
  const [featuredPos, setFeaturedPos] = useState(() => localStorage.getItem('admin_featuredPos')       || 'top');
  useEffect(() => { localStorage.setItem('admin_gridCols',   gridCols);    }, [gridCols]);
  useEffect(() => { localStorage.setItem('admin_featuredPos', featuredPos); }, [featuredPos]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [arts, vids, feat] = await Promise.all([
        fetchArticles(), fetchHelpArticles(), fetchFeaturedVideos(),
      ]);
      setArticles(arts);
      setVideos(vids);
      setFeatured(feat);
    } catch (err) {
      console.error('Load error:', err);
      showToast('Failed to load resources.', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  if (!isOpen) return null;

  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[gridCols];

  // ── Move Up/Down ──────────────────────────────────────────────────────────
  const handleMove = async (type, index, direction, list, setter) => {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;

    const a = list[index];
    const b = list[swapIdx];
    const orderA = a.sort_order ?? index + 1;
    const orderB = b.sort_order ?? swapIdx + 1;

    const updated = list.map(item => {
      if (item.id === a.id) return { ...item, sort_order: orderB };
      if (item.id === b.id) return { ...item, sort_order: orderA };
      return item;
    }).sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0));
    setter(updated);

    try {
      await Promise.all([
        updateItemSortOrder(type, a.id, orderB),
        updateItemSortOrder(type, b.id, orderA),
      ]);
    } catch {
      showToast('Failed to save order', 'error');
    }
  };

  // ── Ingest handlers ───────────────────────────────────────────────────────
  const handleIngestArticles = async () => {
    setIngestingArt(true);
    try {
      let count = 0;
      for (let i = 0; i < mockResources.length; i++) {
        const art = mockResources[i];
        await createArticle({
          title:           art.title,
          category:        art.category,
          excerpt:         art.description,
          content:         `<p>${art.description}</p>`,
          cover_image_url: art.image || '',
          tags:            (art.tags || []).join(', '),
          status:          'published',
          is_featured:     0,
          sort_order:      i + 1,
        });
        count++;
      }
      showToast(`${count} mock articles loaded!`);
      await load();
    } catch (err) {
      console.error('Article ingest error:', err);
      showToast('Failed to ingest articles.', 'error');
    }
    setIngestingArt(false);
  };

  const handleIngestVideos = async () => {
    setIngestingVid(true);
    try {
      let count = 0;
      for (const vid of MOCK_VIDEOS) {
        await createHelpArticle({
          title:       vid.title,
          content:     vid.content,
          category:    vid.category,
          youtube_url: vid.youtube_url,
          icon:        vid.icon,
          is_published: 1,
          sort_order:   vid.sort_order,
        });
        count++;
      }
      showToast(`${count} mock videos loaded!`);
      await load();
    } catch (err) {
      console.error('Video ingest error:', err);
      showToast('Failed to ingest videos.', 'error');
    }
    setIngestingVid(false);
  };

  // ── Article CRUD ──────────────────────────────────────────────────────────
  const handleSaveArticle = async (form) => {
    setSaving(true);
    try {
      if (editingItem?.id) {
        await updateArticle(editingItem.id, form);
        // Refresh from server to get authoritative data
        const refreshed = await fetchArticles();
        setArticles(refreshed);
        showToast('Article updated!');
      } else {
        await createArticle(form);
        const refreshed = await fetchArticles();
        setArticles(refreshed);
        showToast('Article created!');
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Save article error:', err);
      showToast(`Failed to save article: ${err.message}`, 'error');
    }
    setSaving(false);
  };

  const handleDeleteArticle = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteArticle(deletingItem.id);
      setArticles(prev => prev.filter(a => a.id !== deletingItem.id));
      showToast('Article deleted.');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
    setDeletingItem(null);
    setDeleting(false);
  };

  // ── Video CRUD ────────────────────────────────────────────────────────────
  const handleSaveVideo = async (form) => {
    setSaving(true);
    try {
      if (editingItem?.id) {
        await updateHelpArticle(editingItem.id, form);
        const refreshed = await fetchHelpArticles();
        setVideos(refreshed);
        showToast('Video updated!');
      } else {
        await createHelpArticle(form);
        const refreshed = await fetchHelpArticles();
        setVideos(refreshed);
        showToast('Video added!');
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Save video error:', err);
      showToast(`Failed to save video: ${err.message}`, 'error');
    }
    setSaving(false);
  };

  const handleDeleteVideo = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteHelpArticle(deletingItem.id);
      setVideos(prev => prev.filter(v => v.id !== deletingItem.id));
      showToast('Video deleted.');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
    setDeletingItem(null);
    setDeleting(false);
  };

  // ── Featured CRUD ─────────────────────────────────────────────────────────
  const handleSaveFeatured = async (form) => {
    setSaving(true);
    try {
      if (editingItem?.id) {
        await updateFeaturedVideo(editingItem.id, form);
        const refreshed = await fetchFeaturedVideos();
        setFeatured(refreshed);
        showToast('Featured video updated!');
      } else {
        await createFeaturedVideo(form);
        const refreshed = await fetchFeaturedVideos();
        setFeatured(refreshed);
        showToast('Video featured successfully!');
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Save featured error:', err);
      showToast(`Failed to save featured video: ${err.message}`, 'error');
    }
    setSaving(false);
  };

  const handleDeleteFeatured = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteFeaturedVideo(deletingItem.id);
      setFeatured(prev => prev.filter(f => f.id !== deletingItem.id));
      showToast('Featured video removed.');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
    setDeletingItem(null);
    setDeleting(false);
  };

  const handleConfirmDelete = () => {
    if (tab === 'articles') return handleDeleteArticle();
    if (tab === 'videos')   return handleDeleteVideo();
    if (tab === 'featured') return handleDeleteFeatured();
  };

  const filteredArticles = filterCat === 'All' ? articles : articles.filter(a => a.category === filterCat);

  const youtubeThumb = (url) => {
    const m = url?.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
    return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
  };

  const TABS = [
    { id:'articles', label:'Articles',       icon:FiFileText, count:articles.length, color:'text-accent-navy'  },
    { id:'videos',   label:'Videos',         icon:FiVideo,    count:videos.length,   color:'text-red-500'      },
    { id:'featured', label:'Featured Video', icon:FiStar,     count:featured.length, color:'text-accent-gold'  },
    { id:'layout',   label:'Layout',         icon:FiGrid,     count:null,            color:'text-accent-navy'  },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[250] flex items-start justify-center overflow-y-auto py-8 px-4">
        <motion.div initial={{ opacity:0,y:30 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:30 }}
          className="bg-white rounded-[40px] w-full max-w-6xl shadow-2xl">

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-brand-dark bg-accent-navy rounded-t-[40px]">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-accent-gold rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="w-5 h-5 text-accent-navy" />
              </div>
              <div>
                <h2 className="font-serif text-white text-2xl">Resources Editor</h2>
                <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Admin · Rich Text Enabled</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={load} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors" title="Refresh">
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-white" />
              </button>
              <button onClick={onClose} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-8 pt-6 pb-0 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-t-2xl text-[10px] font-black uppercase tracking-widest border-2 border-b-0 transition-all whitespace-nowrap ${tab === t.id ? 'bg-white border-brand-dark text-accent-navy' : 'bg-brand-light border-transparent text-text-muted hover:text-accent-navy'}`}>
                <SafeIcon icon={t.icon} className={`w-3.5 h-3.5 ${tab === t.id ? t.color : ''}`} />
                {t.label}
                {t.count !== null && (
                  <span className="bg-accent-navy/10 text-accent-navy px-1.5 py-0.5 rounded-full text-[8px]">{t.count}</span>
                )}
              </button>
            ))}
          </div>
          <div className="border-t-2 border-brand-dark mx-8" />

          {/* Body */}
          <div className="p-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-10 h-10 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-text-muted text-sm font-light">Loading resources…</p>
              </div>

            ) : tab === 'articles' ? (
              /* ── ARTICLES TAB ── */
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {['All', ...ARTICLE_CATEGORIES].map(cat => (
                      <button key={cat} onClick={() => setFilterCat(cat)}
                        className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${filterCat === cat ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {articles.length > 0 && (
                      <button onClick={handleIngestArticles} disabled={ingestingArt}
                        className="flex items-center gap-2 px-5 py-3 bg-brand-light text-accent-navy border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-accent-navy transition-all disabled:opacity-50">
                        {ingestingArt ? <><Spinner color="border-accent-navy" /> Loading…</> : <><SafeIcon icon={FiDownloadCloud} className="w-4 h-4" /> Add Mock</>}
                      </button>
                    )}
                    <button onClick={() => { setEditingItem(null); setShowForm(true); }}
                      className="flex items-center gap-2 px-6 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all whitespace-nowrap">
                      <SafeIcon icon={FiPlus} className="w-4 h-4" /> New Article
                    </button>
                  </div>
                </div>

                {filteredArticles.length === 0 ? (
                  <IngestBanner label="Articles" icon={FiFileText} onIngest={handleIngestArticles} loading={ingestingArt} />
                ) : (
                  <div className="space-y-3">
                    {filteredArticles.map((art, idx) => (
                      <div key={art.id}
                        className="flex items-start gap-4 p-5 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-navy/30 transition-all group">
                        {art.cover_image_url ? (
                          <img src={art.cover_image_url} alt={art.title}
                            className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => { e.target.style.display='none'; }} />
                        ) : (
                          <div className="w-16 h-16 bg-accent-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <SafeIcon icon={FiFileText} className="w-6 h-6 text-accent-navy/40" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-[8px] font-black uppercase tracking-widest bg-accent-navy/10 text-accent-navy px-2 py-0.5 rounded-full">{art.category}</span>
                            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${art.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {art.status || 'published'}
                            </span>
                            {!!art.is_featured && <SafeIcon icon={FiStar} className="w-3.5 h-3.5 text-accent-gold" />}
                          </div>
                          <h4 className="font-serif text-accent-navy text-base font-bold truncate">{art.title}</h4>
                          {art.excerpt && <p className="text-xs text-text-muted font-light line-clamp-1 mt-0.5">{art.excerpt}</p>}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleMove('articles', idx, 'up', filteredArticles, setArticles)}
                              disabled={idx === 0}
                              className="p-1 bg-white rounded-lg border border-brand-dark hover:border-accent-navy disabled:opacity-30 transition-all">
                              <SafeIcon icon={FiChevronUp} className="w-3 h-3 text-accent-navy" />
                            </button>
                            <button onClick={() => handleMove('articles', idx, 'down', filteredArticles, setArticles)}
                              disabled={idx === filteredArticles.length - 1}
                              className="p-1 bg-white rounded-lg border border-brand-dark hover:border-accent-navy disabled:opacity-30 transition-all">
                              <SafeIcon icon={FiChevronDown} className="w-3 h-3 text-accent-navy" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditingItem(art); setShowForm(true); }}
                              className="p-2 bg-white rounded-xl border border-brand-dark hover:border-accent-navy transition-all">
                              <SafeIcon icon={FiEdit3} className="w-4 h-4 text-accent-navy" />
                            </button>
                            <button onClick={() => setDeletingItem(art)}
                              className="p-2 bg-white rounded-xl border border-brand-dark hover:border-red-400 transition-all">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            ) : tab === 'videos' ? (
              /* ── VIDEOS TAB ── */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-muted font-light">Manage YouTube videos in the Help & Guidance section.</p>
                  <div className="flex items-center gap-3">
                    {videos.length > 0 && (
                      <button onClick={handleIngestVideos} disabled={ingestingVid}
                        className="flex items-center gap-2 px-5 py-3 bg-brand-light text-accent-navy border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-accent-navy transition-all disabled:opacity-50">
                        {ingestingVid ? <><Spinner color="border-accent-navy" /> Loading…</> : <><SafeIcon icon={FiDownloadCloud} className="w-4 h-4" /> Add Mock</>}
                      </button>
                    )}
                    <button onClick={() => { setEditingItem(null); setShowForm(true); }}
                      className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all">
                      <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Video
                    </button>
                  </div>
                </div>

                {videos.length === 0 ? (
                  <IngestBanner label="Videos" icon={FiVideo} onIngest={handleIngestVideos} loading={ingestingVid} />
                ) : (
                  <div className="space-y-3">
                    {videos.map((vid, idx) => (
                      <div key={vid.id}
                        className="flex items-center gap-4 p-5 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-navy/30 transition-all group">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0 text-xl">{vid.icon || '🎥'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[8px] font-black uppercase tracking-widest bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{vid.category || 'Uncategorised'}</span>
                            <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${vid.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {vid.is_published ? 'Published' : 'Hidden'}
                            </span>
                            {vid.is_featured ? <SafeIcon icon={FiStar} className="w-3 h-3 text-accent-gold" /> : null}
                          </div>
                          <h4 className="font-bold text-accent-navy text-sm truncate">{vid.title}</h4>
                          {vid.youtube_url && <p className="text-[10px] text-text-muted truncate font-mono mt-0.5">{vid.youtube_url}</p>}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleMove('videos', idx, 'up', videos, setVideos)}
                              disabled={idx === 0}
                              className="p-1 bg-white rounded-lg border border-brand-dark hover:border-accent-navy disabled:opacity-30 transition-all">
                              <SafeIcon icon={FiChevronUp} className="w-3 h-3 text-accent-navy" />
                            </button>
                            <button onClick={() => handleMove('videos', idx, 'down', videos, setVideos)}
                              disabled={idx === videos.length - 1}
                              className="p-1 bg-white rounded-lg border border-brand-dark hover:border-accent-navy disabled:opacity-30 transition-all">
                              <SafeIcon icon={FiChevronDown} className="w-3 h-3 text-accent-navy" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditingItem(vid); setShowForm(true); }}
                              className="p-2 bg-white rounded-xl border border-brand-dark hover:border-accent-navy transition-all">
                              <SafeIcon icon={FiEdit3} className="w-4 h-4 text-accent-navy" />
                            </button>
                            <button onClick={() => setDeletingItem(vid)}
                              className="p-2 bg-white rounded-xl border border-brand-dark hover:border-red-400 transition-all">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            ) : tab === 'featured' ? (
              /* ── FEATURED VIDEO TAB ── */
              <div className="space-y-6">
                <div className="flex items-start gap-3 p-4 bg-accent-gold/10 border border-accent-gold/20 rounded-2xl">
                  <SafeIcon icon={FiStar} className="w-5 h-5 text-accent-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-accent-navy">Featured Videos Spotlight</p>
                    <p className="text-xs text-text-muted font-light mt-0.5">
                      Create a brand-new featured entry or promote any existing library video. Use the Layout tab to control positioning.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-muted font-light">{featured.length} featured video{featured.length !== 1 ? 's' : ''}</p>
                  <button onClick={() => { setEditingItem(null); setShowForm(true); }}
                    className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/80 transition-all">
                    <SafeIcon icon={FiPlus} className="w-4 h-4" /> Feature a Video
                  </button>
                </div>

                {featured.length === 0 ? (
                  <div className="text-center py-16 bg-brand-light rounded-[28px] border-2 border-dashed border-brand-dark">
                    <SafeIcon icon={FiStar} className="w-12 h-12 text-text-muted/20 mx-auto mb-3" />
                    <p className="font-serif text-accent-navy text-lg mb-1">No featured videos yet</p>
                    <p className="text-text-muted font-light text-sm">Click "Feature a Video" to get started.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {featured.map((fv, idx) => {
                      const thumb = youtubeThumb(fv.youtube_url);
                      return (
                        <div key={fv.id}
                          className="bg-brand-light rounded-2xl border border-brand-dark overflow-hidden group hover:border-accent-gold/50 transition-all">
                          <div className="relative h-40 bg-black overflow-hidden">
                            {thumb ? (
                              <img src={thumb} alt={fv.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <SafeIcon icon={FiVideo} className="w-10 h-10 text-white/30" />
                              </div>
                            )}
                            <div className="absolute top-2 left-2 flex gap-1.5">
                              <span className="bg-accent-gold text-accent-navy px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                                <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> #{idx + 1}
                              </span>
                              {!fv.is_active && (
                                <span className="bg-gray-700 text-white px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest">Inactive</span>
                              )}
                            </div>
                            {fv.promoted_from_id && (
                              <div className="absolute top-2 right-2 bg-white/90 text-accent-navy px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest">Promoted</div>
                            )}
                            <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => { setEditingItem(fv); setShowForm(true); }}
                                className="p-1.5 bg-white rounded-lg shadow-md hover:bg-accent-navy hover:text-white transition-all">
                                <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5 text-accent-navy" />
                              </button>
                              <button onClick={() => setDeletingItem(fv)}
                                className="p-1.5 bg-white rounded-lg shadow-md hover:bg-red-500 hover:text-white transition-all">
                                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5 text-red-500" />
                              </button>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-lg">{fv.icon || '⭐'}</span>
                              <span className="text-[8px] font-black uppercase tracking-widest bg-red-50 text-red-500 px-2 py-0.5 rounded-full">{fv.category}</span>
                              <span className="text-[8px] text-text-muted">Order: {fv.sort_order}</span>
                            </div>
                            <h4 className="font-bold text-accent-navy text-sm line-clamp-1">{fv.title}</h4>
                            {fv.description && <p className="text-xs text-text-muted font-light line-clamp-2 mt-1">{fv.description}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            ) : (
              /* ── LAYOUT TAB ── */
              <div className="space-y-8">
                <div>
                  <h3 className="font-serif text-accent-navy text-xl mb-1">Article Grid Columns</h3>
                  <p className="text-sm text-text-muted font-light mb-5">Control how many columns the article grid displays.</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {GRID_OPTIONS.map(opt => (
                      <button key={opt.cols} onClick={() => setGridCols(opt.cols)}
                        className={`p-5 rounded-2xl border-2 text-center transition-all ${gridCols === opt.cols ? 'border-accent-gold bg-accent-gold/5 shadow-lg' : 'border-brand-dark bg-brand-light hover:border-accent-navy/40'}`}>
                        <div className="grid gap-1 mb-3 mx-auto w-16" style={{ gridTemplateColumns:`repeat(${opt.cols}, 1fr)` }}>
                          {Array.from({ length: opt.cols }).map((_, i) => (
                            <div key={i} className="h-8 bg-accent-navy/20 rounded" />
                          ))}
                        </div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{opt.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-serif text-accent-navy text-xl mb-1">Featured Video Position</h3>
                  <p className="text-sm text-text-muted font-light mb-5">Where the featured video appears relative to the video grid.</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {FEATURED_POSITIONS.map(pos => (
                      <button key={pos.id} onClick={() => setFeaturedPos(pos.id)}
                        className={`p-5 rounded-2xl border-2 text-center transition-all ${featuredPos === pos.id ? 'border-accent-gold bg-accent-gold/5 shadow-lg' : 'border-brand-dark bg-brand-light hover:border-accent-navy/40'}`}>
                        <div className="text-3xl mb-2">{pos.icon}</div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{pos.label}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-text-muted mt-3 font-light">
                    ✓ Position preference is saved and persists across sessions.
                  </p>
                </div>

                {articles.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4">Live Article Grid Preview</h4>
                    <div className={`grid ${gridClass} gap-4`}>
                      {articles.slice(0, gridCols * 2).map((art, i) => (
                        <div key={art.id || i} className="bg-brand-light rounded-2xl overflow-hidden border border-brand-dark">
                          {art.cover_image_url && (
                            <div className="h-28 overflow-hidden">
                              <img src={art.cover_image_url} alt={art.title} className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display='none'; }} />
                            </div>
                          )}
                          <div className="p-4">
                            <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mb-1">{art.category}</p>
                            <p className="font-serif text-accent-navy text-sm font-bold line-clamp-2">{art.title}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* ── Form Modals ── */}
      <AnimatePresence>
        {showForm && tab === 'articles' && (
          <ArticleFormModal article={editingItem} onSave={handleSaveArticle}
            onClose={() => { setShowForm(false); setEditingItem(null); }} saving={saving} />
        )}
        {showForm && tab === 'videos' && (
          <VideoFormModal video={editingItem} onSave={handleSaveVideo}
            onClose={() => { setShowForm(false); setEditingItem(null); }} saving={saving} />
        )}
        {showForm && tab === 'featured' && (
          <FeaturedVideoFormModal featuredVideo={editingItem} existingVideos={videos}
            onSave={handleSaveFeatured}
            onClose={() => { setShowForm(false); setEditingItem(null); }} saving={saving} />
        )}
        {deletingItem && (
          <DeleteConfirm item={deletingItem} onConfirm={handleConfirmDelete}
            onCancel={() => setDeletingItem(null)} deleting={deleting} />
        )}
      </AnimatePresence>

      {/* ── Toast ── */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:20 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[500] px-6 py-3 rounded-2xl shadow-xl text-sm font-bold flex items-center gap-2 ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-accent-navy text-white'}`}>
            <SafeIcon icon={toast.type === 'error' ? FiAlertTriangle : FiCheck} className="w-4 h-4" />
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminResourceEditor;